import config from "config/config";
import { getToken } from "js/formatting/getToken";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import SimpleDataRow from "components/General/SimpleDataRow";
import CustomerRowPagination from "components/General/CustomerRowPagination";
import FormInputError from "components/General/FormInputError";
import InputWithActionButton from "components/General/InputWithActionButton";
import PillWithActionButton from "components/General/PillWithActionButton";
import AutoCompleteComboBox from "components/General/AutoCompleteComboBox";
import { useRowPagination } from "hooks/useRowPagination";
import { getCSRForms } from "service/forms/getCSRForms";
import { getPolicyDetails } from "service/policy/getPolicyDetails";
import { useSelector } from "react-redux";
import { isAgentPortal, isCSR, isCSRAdmin } from "js/utilities";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import { sendEmailNotification_v2 } from "service/notificationservice/sendEmailNotification";

export default function CSRFormsPanel() {
  const { t } = useTranslation();
  const {
    register,
    watch,
    reset,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
  } = useForm();

  useBreadcrumb([
    {
      name: "Customer lookup",
      url: "/policies",
    },
    {
      name: "Forms page",
      url: "#",
    },
  ]);

  const { csrUser } = useSelector((state) => state.csrUser);
  const isCSRUser = isCSR(csrUser) || isCSRAdmin(csrUser);

  const [isLoading, setIsLoading] = useState(false);
  const [searchedPolicyId, setSearchedPolicyId] = useState("");
  const [hasSubmittedSearch, setHasSubmittedSearch] = useState(false);
  const [allForms, setAllForms] = useState([]);
  const [forms, setForms] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [selectedContractType, setSelectedContractType] = useState("all");
  const [searchByKeyWords, setSearchByKeyWords] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendEmailError, setSendEmailError] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [sendEmailSuccessMessage, setSendEmailSuccessMessage] = useState("");
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [companyFilterOptions, setCompanyFilterOptions] = useState([
    { value: "all", displayAs: "Select Company" },
  ]);
  const [products] = useState([
    { value: "life", displayAs: "Life" },
    { value: "annuity", displayAs: "Annuity" },
  ]);
  const [contractTypes] = useState([
    { value: "both", displayAs: "Both" },
    { value: "fixed", displayAs: "Fixed" },
    { value: "variable", displayAs: "Variable" },
  ]);

  const {
    rowsForDisplay: formsRows,
    rowPaginationProps: formsPaginationProps,
  } = useRowPagination(forms);

  const policyIdInputValue = watch("policyId", "");
  const trimmedPolicyIdInputValue = policyIdInputValue?.trim() || "";
  const portal = isAgentPortal() ? "agent" : "customer";

  const getCompanyNameByAbbreviation = (abbreviation) => {
    if (!abbreviation) {
      return abbreviation;
    }

    const fallbackCompanyNames = {
      EAC: "Everlake Assurance Company",
      ELIC: "Everlake Life Insurance Company",
      LBL: "Lincoln Benefit Life Company",
      SLIC: "Surety Life Insurance Company",
      AHL: "American Heritage Life Insurance Company",
      AML: "American Maturity Life Insurance Company",
    };

    try {
      const companyMap = JSON.parse(config.COMPANY_BLOCK_CODES);

      if (companyMap?.[abbreviation]) {
        return companyMap[abbreviation];
      }

      const matchedEntry = Object.entries(companyMap).find(
        ([, companyAbbreviation]) => companyAbbreviation === abbreviation
      );

      if (matchedEntry?.[0]) {
        return matchedEntry[0];
      }
    } catch (error) {}

    return fallbackCompanyNames[abbreviation] || abbreviation;
  };

  const getDistinctCategory = (availableForms) => {
    const uniqueCategories = new Map();

    availableForms.forEach((form) => {
      if (!form?.category || form?.categoryId == null) {
        return;
      }

      const categoryKey = `${form.categoryId}-${form.category}`;

      if (!uniqueCategories.has(categoryKey)) {
        uniqueCategories.set(categoryKey, {
          category: form.category,
          categoryId: form.categoryId,
        });
      }
    });

    return Array.from(uniqueCategories.values()).sort(
      (firstCategory, secondCategory) =>
        firstCategory.category.localeCompare(
          secondCategory.category,
          undefined,
          {
            sensitivity: "base",
          }
        )
    );
  };

  const getCompanyNameFromForms = (availableForms, abbreviation) => {
    const matchedForm = availableForms.find(
      (form) =>
        form?.company === abbreviation &&
        form?.formName?.includes(" - ") &&
        form?.formName?.split(" - ").at(-1)?.trim()
    );

    return matchedForm?.formName?.split(" - ").at(-1)?.trim();
  };

  const getDistinctCompanies = (availableForms) => {
    const companies = [
      ...new Set(availableForms.map((form) => form.company).filter(Boolean)),
    ];

    return [
      { value: "all", displayAs: "Select Company" },
      ...companies.map((company) => ({
        value: company,
        displayAs:
          getCompanyNameFromForms(availableForms, company) ||
          getCompanyNameByAbbreviation(company),
      })),
    ];
  };

  const addKeyWord = (searchKeyword) => {
    const keyWord = searchKeyword?.trim();

    if (keyWord && !searchByKeyWords.includes(keyWord)) {
      setSearchByKeyWords((current) => [...current, keyWord]);
    }
  };

  const getFormSortTitle = (form) =>
    form?.formTitle?.trim() ||
    form?.formName?.trim() ||
    form?.formSubTitle?.trim() ||
    "";

  const sortFormsAlphabetically = (availableForms = []) =>
    [...availableForms].sort((firstForm, secondForm) =>
      getFormSortTitle(firstForm).localeCompare(
        getFormSortTitle(secondForm),
        undefined,
        {
          sensitivity: "base",
        }
      )
    );

  const filterByKeywords = (form, keywords) => {
    let formFound;
    const formKeywords = form?.keywords?.toLowerCase() ?? "";

    keywords.forEach((keyword) => {
      const splitKeywords = keyword.split(/\s+/g);
      splitKeywords.forEach((searchTerm) => {
        if (formKeywords.includes(searchTerm.toLowerCase())) {
          formFound = form;
        }
      });
    });

    return formFound;
  };

  const filteredForms = (
    availableForms,
    company,
    category,
    product,
    contractType,
    keywords
  ) => {
    if (!hasSubmittedSearch || !availableForms?.length) {
      setForms([]);
      return;
    }

    let filteredFormsList = [...availableForms];

    if (company !== "all" && company?.value !== "all") {
      filteredFormsList = filteredFormsList.filter(
        (form) => form?.company === company?.value
      );
    }

    if (category && category !== "all") {
      filteredFormsList = filteredFormsList.filter(
        (form) => String(form?.categoryId) === String(category)
      );
    }

    if (product && product !== "all") {
      filteredFormsList = filteredFormsList.filter(
        (form) => form?.productType?.toLowerCase() === product.toLowerCase()
      );
    }

    if (contractType && contractType !== "all") {
      filteredFormsList = filteredFormsList.filter(
        (form) =>
          form?.contractType &&
          form.contractType.toLowerCase() === contractType.toLowerCase()
      );
    }

    if (keywords?.length > 0 && filteredFormsList?.length > 0) {
      filteredFormsList = filteredFormsList.filter((form) => {
        const formFound = filterByKeywords(form, keywords);
        if (formFound) return formFound;
      });
    }

    setForms(sortFormsAlphabetically(filteredFormsList));
  };

  const resetFilterState = () => {
    setSelectedCategory("all");
    setSelectedCompany("all");
    setSelectedProduct("all");
    setSelectedContractType("all");
    setSearchByKeyWords([]);
    reset({ policyId: trimmedPolicyIdInputValue, keyword: "" });
  };

  const fetchAllCSRForms = async () => {
    const options = {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ClientSecret: config.PORTAL_CLIENT_SECRET,
        Authorization: `bearer ${getToken()}`,
      },
    };

    const queryParams = new URLSearchParams({
      Portal: portal,
      isCsr: "true",
    });

    const response = await fetch(
      `${config.REG_API_BASE_URL}/Forms?${queryParams.toString()}`,
      options
    );

    if (!response.ok) {
      throw response;
    }

    return response.json();
  };

  const createSearchPolicyFetchOptions = (memberID) => ({
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": config.ODS_API_KEY,
      Authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify({
      gXdpApi: {
        transactionType: {
          sourceNameSpace: "ODS",
          targetNameSpace: "GIDP",
          eventName: "SEARCHPOLICY",
          operation: "GET",
        },
        gIdpPayLoad: {
          policyHolder: {
            memberID,
          },
          addOnProperties: {
            company: config.COMPANY,
            clientName: config.CLIENTNAME,
            system: "LSC",
            lifeSysDataset: "P",
            searchType: "IN",
            recordsRequested: 100,
          },
        },
        parentCorrelationID: new Date().toISOString(),
        correlationID: new Date().toISOString(),
      },
    }),
  });

  const searchPolicyByMemberId = async (memberID) => {
    const response = await fetch(
      `${config.ODS_API_BASE_URL}/ce-callerVerification/SearchPolicy`,
      createSearchPolicyFetchOptions(memberID)
    );

    if (!response.ok) {
      throw response;
    }

    return response.json();
  };

  const applyLoadedForms = (responseForms) => {
    const sortedForms = sortFormsAlphabetically(responseForms);

    setAllForms(sortedForms);
    setCategoryOptions(getDistinctCategory(sortedForms));
    setCompanyFilterOptions(getDistinctCompanies(sortedForms));
  };

  const getContractTypeFromProductType = (policyProductType) => {
    const productType = policyProductType?.toUpperCase?.();

    const isVariablePolicy =
      productType === "LQ" ||
      productType === "LZ" ||
      productType === "AVD" ||
      productType === "LZR";

    return isVariablePolicy ? "Variable" : "Fixed";
  };

  const loadForms = async (policyId) => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      if (!policyId) {
        const response = await fetchAllCSRForms();
        const responseForms = response?.form ?? response?.forms ?? [];
        applyLoadedForms(responseForms);
        return;
      }

      const searchPolicyResponse = await searchPolicyByMemberId(policyId);
      const resolvedPolicyId =
        searchPolicyResponse?.gXdpApi?.gIdpPayLoad?.policyHolder?.policies?.[0]
          ?.contractID || "";

      if (!resolvedPolicyId) {
        setErrorMessage("Policy details not available");
        setAllForms([]);
        return;
      }

      const policyDetails = await getPolicyDetails(resolvedPolicyId);

      if (policyDetails === "Error" || !policyDetails) {
        setErrorMessage("Policy details not available");
        setAllForms([]);
        return;
      }

      const company = policyDetails?.blockCode;
      const issueState = policyDetails?.issueState;
      const productType = policyDetails?.product?.type;
      const contractType = getContractTypeFromProductType(productType);
      const oldPolicy = policyDetails?.oldPolicy;

      const insuredState = policyDetails?.parties?.filter(
        (party) => party?.person?.type?.toLowerCase() === "insured"
      );
      const residentState = insuredState
        ?.map((party) => party?.address?.state)
        .filter(Boolean)
        .join(",");

      const response = await getCSRForms(
        oldPolicy,
        portal,
        company,
        issueState,
        residentState,
        productType,
        contractType
      );
      const responseForms = response?.form ?? response?.forms ?? [];
      applyLoadedForms(responseForms);
    } catch (error) {
      if (policyId) {
        setErrorMessage("Policy details not available");
      } else {
        setErrorMessage(t("CustomerForms-errorMessage"));
      }
      setAllForms([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onKeywordSubmit = async (data, e) => {
    addKeyWord(data?.keyword);
    e.target.reset();
  };

  const onPolicySubmit = async (data) => {
    const policyId = data?.policyId?.trim() || "";

    setHasSubmittedSearch(true);
    setSearchedPolicyId(policyId);
    setAllForms([]);
    setForms([]);
    setCategoryOptions([]);
    setCompanyFilterOptions([{ value: "all", displayAs: "Select Company" }]);
    setErrorMessage("");
    setSelectedCategory("all");
    setSelectedCompany("all");
    setSelectedProduct("all");
    setSelectedContractType("all");
    setSearchByKeyWords([]);

    await loadForms(policyId);
  };

  const removeSearchKeyWord = (search, value) => {
    const index = search.indexOf(value);
    if (index > -1) {
      search.splice(index, 1);
    }
    return search;
  };

  const removeKeyWord = (search) => {
    const value = removeSearchKeyWord(searchByKeyWords, search);
    if (value?.length > 0) setSearchByKeyWords((curr) => [...curr]);
    else setSearchByKeyWords([]);
    reset({ policyId: searchedPolicyId, keyword: "" });
    const siteSearchInput = document.getElementById("Site Search");
    if (siteSearchInput) {
      siteSearchInput.value = "";
    }
  };

  const closeSendEmailDialog = () => {
    setIsSendEmailDialogOpen(false);
    setRecipientEmail("");
    setSendEmailError("");
    setSendEmailSuccessMessage("");
    setSelectedFormId(null);
  };

  const handleSendEmail = async () => {
    const trimmedEmail = recipientEmail.trim();
    const emailPattern = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

    if (!trimmedEmail) {
      setSendEmailError("Email is required.");
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setSendEmailError("Please enter a valid email address.");
      return;
    }

    setIsSendingEmail(true);
    setSendEmailError("");
    setSendEmailSuccessMessage("");

    try {
      await sendEmailNotification_v2(trimmedEmail, selectedFormId);
      setSendEmailSuccessMessage("Email sent successfully.");
      setRecipientEmail("");
    } catch (error) {
      setSendEmailError("Failed to send email.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    filteredForms(
      allForms,
      selectedCompany,
      selectedCategory,
      selectedProduct,
      selectedContractType,
      searchByKeyWords
    );
    clearErrors();
  }, [
    allForms,
    selectedCompany,
    selectedCategory,
    selectedProduct,
    selectedContractType,
    searchByKeyWords,
    hasSubmittedSearch,
    clearErrors,
  ]);

  useEffect(() => {
    if (!trimmedPolicyIdInputValue && searchedPolicyId) {
      setSearchedPolicyId("");
    }
  }, [trimmedPolicyIdInputValue, searchedPolicyId]);

  const showFilterSection =
    hasSubmittedSearch &&
    (categoryOptions?.length > 0 ||
      companyFilterOptions?.length > 1 ||
      searchByKeyWords?.length > 0);

  return (
    <div className="container mb-8">
      <h1>{t("Forms-title")}</h1>
      <p>{t("CSRForms-subtitle")}</p>

      <div className="mx-auto w-full md:w-3/5 xl:w-1/2 mt-4">
        <form
          className="flex flex-col sm:flex-row"
          onSubmit={handleSubmit(onPolicySubmit)}
        >
          <div className="flex flex-col flex-grow justify-start sm:mr-4">
            <div className="relative">
              <input
                id="ID_policyId"
                type="text"
                placeholder="Enter contract number"
                aria-placeholder="Enter contract number"
                aria-labelledby="ID_policyIdLabel"
                aria-describedby="ID_policyIdError"
                className="border border-ui-gray-medium h-9 w-full p-2 pl-8 rounded"
                {...register("policyId")}
              />
              <svg
                className="ui-icon w-4 h-4 absolute text-theme-action left-0 top-1/2 mt-[-.5rem] ml-2"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-search"></use>
              </svg>
            </div>
            <div id="ID_policyIdError">
              {errors.policyId && (
                <div className="ui-input-error">{errors.policyId.message}</div>
              )}
            </div>
          </div>
          <div>
            <button
              className="ui-btn-primary h-9 w-32 mx-auto ui-btn-min-w mt-4 sm:mt-0"
              disabled={isLoading}
            >
              Submit
            </button>
          </div>
        </form>
      </div>

      <Dialog
        open={isSendEmailDialogOpen}
        onClose={closeSendEmailDialog}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog max-w-md w-full"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              Send Email
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label="close send email dialog"
              onClick={closeSendEmailDialog}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <hr />
            <div className="ui-dialog-body">
              <label
                id="send_email_input_label"
                htmlFor="send_email_input"
                className="block mb-2"
              >
                Recipient's Email Address
              </label>
              <input
                id="send_email_input"
                type="email"
                aria-labelledby="send_email_input_label"
                className="mt-1"
                value={recipientEmail}
                onChange={(e) => {
                  setRecipientEmail(e.target.value);
                  setSendEmailError("");
                  setSendEmailSuccessMessage("");
                }}
                disabled={isSendingEmail}
              />
              {sendEmailError ? (
                <FormInputError errorMessage={sendEmailError} />
              ) : null}
              {sendEmailSuccessMessage ? (
                <p className="mt-2 text-green-700">{sendEmailSuccessMessage}</p>
              ) : null}
            </div>
            <div className="block text-right mt-6">
              <button
                type="button"
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={closeSendEmailDialog}
                disabled={isSendingEmail}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ui-btn-primary ui-btn-min-w mt-4 ml-2"
                onClick={handleSendEmail}
                disabled={isSendingEmail}
              >
                {isSendingEmail ? "Sending..." : "Send"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {hasSubmittedSearch ? (
        <EverlakeLoadingOverlay
          isLoading={isLoading}
          loadingText={t("CustomerForms-loading-text")}
        >
          <div className="mt-4">
            {showFilterSection ? (
              <div className="p-2 md:p-4 bg-white rounded-lg shadow-[2px_4px_4px_0_rgba(0,0,0,0.16)]">
                <div className="grid gap-2 md:gap-x-4 md:grid-cols-[1fr,0.5fr,0.5fr,0.5fr,0.8fr,1fr]">
                  <AutoCompleteComboBox
                    labelName="Company"
                    inputPlaceholderText="Select Company"
                    options={companyFilterOptions}
                    invalidMessage="Please select company."
                    selectedOption={selectedCompany}
                    setSelectedOption={setSelectedCompany}
                  />

                  <div className="relative mt-1">
                    <div className="flex items-center mb-1">
                      <label id="formsDocumentProduct">Product</label>
                    </div>
                    <div className="relative">
                      <select
                        id="productId"
                        className="w-full"
                        value={selectedProduct}
                        onChange={(e) => {
                          setSelectedProduct(e.target.value);
                        }}
                      >
                        <option value="all">All</option>
                        {products.map((product) => (
                          <option
                            key={`csr-product-${product.value}`}
                            value={product.value}
                          >
                            {product.displayAs}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="relative mt-1">
                    <div className="flex items-center mb-1">
                      <label id="formsDocumentContractType">
                        Contract Type
                      </label>
                    </div>
                    <div className="relative">
                      <select
                        id="contractTypeId"
                        className="w-full"
                        value={selectedContractType}
                        onChange={(e) => {
                          setSelectedContractType(e.target.value);
                        }}
                      >
                        <option value="all">All</option>
                        {contractTypes.map((contractType) => (
                          <option
                            key={`csr-contract-type-${contractType.value}`}
                            value={contractType.value}
                          >
                            {contractType.displayAs}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="relative mt-1">
                    <div className="flex items-center mb-1">
                      <label id="formsDocumentCategory">Category</label>
                    </div>
                    <div className="relative">
                      <select
                        id="categoryId"
                        className="w-full"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        <option value="all">
                          {t("CustomerForms-all-category")}
                        </option>
                        {categoryOptions.map((category) => (
                          <option
                            key={`csr-category-${category.categoryId}-${category.category}`}
                            value={category.categoryId}
                          >
                            {category.category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <form
                    onSubmit={handleSubmit(onKeywordSubmit)}
                    className="flex flex-col-reverse md:col-span-2"
                  >
                    <Controller
                      name="keyword"
                      control={control}
                      render={({ field: { onChange } }) => (
                        <InputWithActionButton
                          labelName="Keyword"
                          buttonType="submit"
                          buttonLabel={t("CustomerForms-searchButton")}
                          ariaLabel={t("CustomerForms-searchAriaLabel")}
                          placeholder={t("CustomerForms-searchPlaceholder")}
                          error={t("CustomerForms-searchErroressage")}
                          onChange={onChange}
                        />
                      )}
                      rules={{
                        pattern: {
                          value: /^[A-Za-z0-9() \-]+$/,
                          message: t("CustomerForms-pattern-error-display"),
                        },
                      }}
                    />
                  </form>

                  {errors?.keyword?.message && (
                    <>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                      <FormInputError errorMessage={errors?.keyword?.message} />
                    </>
                  )}
                </div>

                {searchByKeyWords?.length && !isLoading ? (
                  <div className="mt-4 flex flex-start gap-2 ">
                    {searchByKeyWords.map((search, idx) => (
                      <PillWithActionButton
                        key={`search for csr forms - ${idx}`}
                        label={search}
                        action={() => removeKeyWord(search)}
                      />
                    ))}
                  </div>
                ) : null}

                {errorMessage ? (
                  <div className="mt-4">
                    <FormInputError errorMessage={errorMessage} />
                  </div>
                ) : null}
              </div>
            ) : null}

            {formsRows?.length > 0 ? (
              <div className="flex justify-center flex-col p-0 mt-2 container">
                {formsRows.map((form, idx) => (
                  <SimpleDataRow
                    key={`csr-forms-row-${idx}`}
                    data={form}
                    type="form"
                    hideFormSubTitle={true}
                    isCSRUser={isCSRUser}
                    onSendEmailClick={(selectedForm) => {
                      setRecipientEmail("");
                      setSendEmailSuccessMessage("");
                      setSendEmailError("");
                      setSelectedFormId(Number(selectedForm?.id));
                      setIsSendEmailDialogOpen(true);
                    }}
                  />
                ))}
                <CustomerRowPagination {...formsPaginationProps} />
              </div>
            ) : (
              <div className="w-full mt-4 mb-2 md:ml-6 text-center">
                {!isLoading && (
                  <h3>{errorMessage || t("CustomerForms-errorMessage")}</h3>
                )}
              </div>
            )}
          </div>
        </EverlakeLoadingOverlay>
      ) : (
        <div className="w-full mt-8 mb-2 text-center">
          <h3>{t("CustomerForms-displayMessage")}</h3>
        </div>
      )}
    </div>
  );
}
